import type React from "react";
import { isPresent } from "@apl-digital/utils";

import type { IMyCollectionInfo } from "@/api/interfaces/myCollectionInfoLayout";
import { SectionType } from "@/api/interfaces/pageLayout";
import { renderCmsBlocks } from "@/base/blocks/blockRegistry";
import IntroductionBlocks from "@/base/blocks/IntroductionBlocks";
import LocationsBlock from "@/base/blocks/LocationsBlock";
import MyCollectionBlock from "@/base/blocks/MyCollectionBlock/MyCollectionBlock";
import MyInfoBlock from "@/base/blocks/MyInfoBlock";
import PerkBlock from "@/base/blocks/PerkBlock/PerkBlock";
import TransactionsBlock from "@/base/blocks/TransactionsBlock/TransactionsBlock";
import { CmsBlocks } from "@/base/components/Global";
import { CmsPage } from "@/base/components/Global/cmsPage";
import { useMediaQuery } from "@/base/components/MediaQueryProvider";
import config from "@/constants/config";
import { fetchPrivateHomePage } from "@/store/cms";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectCurrentRegionPrefix } from "@/store/region";

const privateRegistry = {
  [SectionType.BENEFITS_DETAILS]: IntroductionBlocks,
  [SectionType.LOCATION]: LocationsBlock,
  [SectionType.MY_COLLECTION]: MyCollectionBlock,
};

const PrivateHomePage: React.FC = () => {
  const [{ isCurrentSizeMd }] = useMediaQuery();
  const privateHomePage = useAppSelector((state) => state.cms.privateHomePage);
  const currentRegionPrefix = useAppSelector(selectCurrentRegionPrefix);
  const dispatch = useAppDispatch();

  return (
    <CmsPage
      name="PrivateHomePage"
      pageStateMachine={privateHomePage}
      fetchPage={() => dispatch(fetchPrivateHomePage())}
    >
      {({ page }) => (
        <CmsBlocks>
          {renderCmsBlocks(page.components, privateRegistry, {
            firstBlockSmall: false,
            overrides: {
              [SectionType.LOYALTY_CARD]: () => (
                <MyInfoBlock
                  mobile={{ showCard: true }}
                  desktop={{ showCard: true }}
                />
              ),
              [SectionType.MY_COLLECTION]: (block) => (
                <MyCollectionBlock block={block as IMyCollectionInfo} />
              ),
              [SectionType.GIFT_CARDS]: (block) => (
                <PerkBlock
                  block={block}
                  showTable={isCurrentSizeMd}
                  showDescription={isCurrentSizeMd}
                />
              ),
              [SectionType.TRANSACTIONS]:
                isPresent(currentRegionPrefix) &&
                !config.featureFlags.enableTransactions.includes(
                  currentRegionPrefix,
                )
                  ? null
                  : (block) => <TransactionsBlock block={block} />,
            },
          })}
        </CmsBlocks>
      )}
    </CmsPage>
  );
};

export default PrivateHomePage;
